import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import { Alert, DialogActions, Grid } from "@mui/material";
import { FormikProps, useFormik } from "formik";
import moment from "moment";
import { useEffect } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useApiFeedback } from "../../../../app/useApiFeedback";
import CsDateTimePicker from "../../../../common/custom/CsDateTimePicker";
import MessageResponse from "../../../../common/feedback/messageResponse";
import { SetApplicationDatesDto } from "../../models";
import { selectAdmin } from "../../adminSlice";
import { setApplicationDatesThunk } from "../../thunks";

interface Props {
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export default function ApplicationDatesForm({ setOpen }: Props) {
  const dispatch = useAppDispatch();
  const adminSelect = useAppSelector(selectAdmin);
  const { feedback, handleApiResponse } = useApiFeedback();

  const validationSchema = yup.object({
    alertStartDate: yup.string().required("Alert start date is required"),
    closingDate: yup.string().required("Closing date is required"),
    openingDate: yup.string().required("Opening date is required"),
  });

  const initialValues = {
    alertStartDate: null,
    closingDate: null,
    openingDate: null,
  } as SetApplicationDatesDto;

  const formik: FormikProps<SetApplicationDatesDto> =
    useFormik<SetApplicationDatesDto>({
      initialValues: initialValues,
      enableReinitialize: true,
      validationSchema: validationSchema,
      onSubmit: (values: SetApplicationDatesDto) => {
        const format = "YYYY/MM/DD HH:mm";
        const payload = {
          alertStartDate: moment(values.alertStartDate?.toString()).format(
            format
          ),
          closingDate: moment(values.closingDate?.toString()).format(format),
          openingDate: moment(values.openingDate?.toString()).format(format),
        } as SetApplicationDatesDto;
        dispatch(setApplicationDatesThunk(payload)).then((data: any) => {
          handleApiResponse(data, true);
        });
      },
    });

  useEffect(() => {
    if (feedback.isSuccess) {
      formik.resetForm();
      setOpen(false);
    }
  }, [feedback]);
  return (
    <Grid container spacing={1} mt={1}>
      <Grid item xs={12}>
        <Alert variant="filled" severity="info">
          Please note that re-setting the dates recycle all the applications
          that fall from the year {new Date().getFullYear() - 1}. This action is
          recommended every year before applications reopen.
        </Alert>
      </Grid>
      <MessageResponse feedback={feedback} />
      <Grid item xs={6}>
        <CsDateTimePicker
          label="Opening Date"
          formik={formik}
          name="openingDate"
          errors={formik.errors.openingDate}
          touched={formik.touched.openingDate}
        />
      </Grid>
      <Grid item xs={6}>
        <CsDateTimePicker
          label="Alert Count Down Date"
          formik={formik}
          name="alertStartDate"
          errors={formik.errors.alertStartDate}
          touched={formik.touched.alertStartDate}
        />
      </Grid>

      <Grid item xs={6}>
        <CsDateTimePicker
          label="Closing Date"
          formik={formik}
          name="closingDate"
          errors={formik.errors.closingDate}
          touched={formik.touched.closingDate}
        />
      </Grid>

      <Grid item xs={12}>
        <DialogActions>
          <LoadingButton
            sx={{ float: "right", marginBottom: 1.5 }}
            color="primary"
            variant="contained"
            size="medium"
            startIcon={<SaveOutlinedIcon />}
            onClick={() => {
              formik.submitForm();
            }}
            loading={adminSelect.applicationDatesState.status === "loading"}
            loadingPosition="start"
          >
            Save
          </LoadingButton>
        </DialogActions>
      </Grid>
    </Grid>
  );
}
